import { Injectable } from '@angular/core';
import { Observable, Subject, forkJoin, of, firstValueFrom } from 'rxjs';
import { BaseResponse } from '@aifs-shared/common/base-response';
import { BaseService } from '@aifs-shared/common/base-service';
import {
    ApplicantDetails,
    ApplicationDetails,
    ApplicationType,
} from '../../applicant-details';
import {
    IApplicationReviewApplication,
    IApplicationReviewBackground,
    IApplicationReviewExperience,
    IApplicationReviewProfile,
    IApplicationReviewSkills,
} from '../../application-review';
import { IApplicationPreference } from '../../application-preferences';
import {
    IApplicationInterview,
    InterviewType,
} from '../../application-interview-details';
import {
    HistoryType,
    IApplicationCriminalSummary,
    ICurrentEducation,
    IHistory,
    ProfessionalHistory,
} from '../../applicant-professional';
import {
    CFTextReviewItem,
    CFTextReviewStatus,
    CFTextReviewTarget,
} from '@ui/cf-text-review/cf-text-review.component';
import { LookupService } from '@aifs-shared/lookup/lookup.service';
import { IApplicationSkills } from '../../application-skill';
import { ReferenceService } from '@aifs-shared/reference/reference.service';
import { IReferenceReview } from '../../../reference/reference';
import {
    DateFormatOptions,
    getDateOrDefault,
    getFormattedDate,
} from '@aifs-shared/date/date-functions';
import { DateTime, Interval } from 'luxon';
import {
    GapEntry,
    TimelineDetails,
    TimelineEntry,
    TimelineGap,
} from './timeline';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { WorkAreaApplicationAssignment } from '../../../work-area/work-area';
import { WorkAreaService } from '../../../work-area/work-area.service';
import { ApplicantService } from '../../applicant.service';

@Injectable({
    providedIn: 'root',
})
export class ReviewService extends BaseService {
    public reviewItemsChange$ = new Subject<CFTextReviewItem>();

    constructor(
        private http: HttpClient,
        private applicantService: ApplicantService,
        private references: ReferenceService,
        private lookup: LookupService,
        private workAreaService: WorkAreaService
    ) {
        super();
    }

    public async fetchReviewDetails(
        jobId: number,
        applicantId: number,
        applicationId: number
    ): Promise<ApplicantReviewDetails | undefined> {
        const applicant = this.applicantService.getApplicantDetails(
            applicantId,
            applicationId
        );
        const application = this.applicantService.getApplicationDetails(
            applicantId,
            applicationId
        );
        const criminalSummary = this.applicantService.getCriminalSummary(
            applicantId,
            applicationId
        );

        const history = this.applicantService.getProfessionalHistory(applicantId);
        const interview = this.applicantService.getInterviewDetails(
            applicantId,
            applicationId
        );
        const preference = this.applicantService.getPreferences(
            applicantId,
            applicationId
        );
        const skills = this.applicantService.getSkills(applicantId);
        const references = this.references.getReferences(
            applicantId,
            applicationId
        );
        const notes = this.getReviewerNotes(applicantId, applicationId);
        const applicationAssignments =
            this.workAreaService.getJobAssignments(jobId);

        try {
            const results = await firstValueFrom(
                forkJoin({
                    applicant,
                    application,
                    history,
                    interview,
                    preference,
                    skills,
                    references,
                    notes,
                    applicationAssignments,
                    criminalSummary,
                })
            );
            const ard = this.populateReviewDetailsFromData([
                results.applicant,
                results.application,
                results.history,
                results.interview,
                results.preference,
                results.skills,
                results.references,
                results.notes,
                results.applicationAssignments,
                results.criminalSummary,
            ]);
            return ard;
        } catch (error) {
            console.error(`Error fetching review details: ${error}`);
            throw error;
        }
    }

    private populateReviewDetailsFromData(
        results: any
    ): ApplicantReviewDetails | undefined {
        const app = results[0] as ApplicantDetails;
        const apl = results[1] as ApplicationDetails;
        const occ = results[2] as ProfessionalHistory;
        const iv = results[3] as IApplicationInterview;
        const pr = results[4] as IApplicationPreference;
        const sk = results[5] as IApplicationSkills;
        const r = results[6] as IReferenceReview;
        const n = results[7] as ReviewerNote[];
        const assignments = results[8] as WorkAreaApplicationAssignment[];
        const criminalSummary = results[9] as IApplicationCriminalSummary;

        if (app == null || apl == null) return;
        let ard: ApplicantReviewDetails = {
            reviewerNotes: n,
            details: {
                profilePicture: app.profileUrl,
                applicantId: app.applicantId,
                applicationId: apl.id,
                firstName: app.firstName,
                middleName: app.middleName,
                lastName: app.lastName,
                pronouns: app.pronouns,
                dateOfBirth: app.dateOfBirth,
                ageOnJuneFirst: app.ageOnJuneFirst,
                email: app.email,
                applicationCountry: app.applicationCountry,
                residenceCountry: app.residenceCountry,
                nationalityCountry: app.nationalityCountry,
                birthCountry: app.birthCountry,
                fulltimeStudent: apl.fulltimeStudent,
                interviewCopied: apl.interviewCopied,
                otherName: app.otherName ? true : false,
                otherNameGiven: app.otherName,
                otherNameReason: app.otherNameReason,
                placedAtEvent: apl.placedAtEvent,
                whyCamp: iv.report?.whyCamp,
            },
            background: {
                emergencyContactRelation: app.emergencyContactRelationship,
                interestsHobbies: iv.report?.familyInterestsHobbies,
                family: iv.report?.relationshipWithFamily,
                friends: iv.report?.yourFriends,
                childcare: iv.report?.childcareExperience,
                childcareExperience: iv.report?.childcare,
                behaviour: iv.report?.behaviour,
                safeguarding: iv.report?.safeguarding,
            },
            application: {
                programme: apl.programme ?? '',
                applicationType: apl.applicationType,
                availableFrom: apl.earliestAvailability ?? '',
                dueHome: apl.dateDueHome ?? '',
                role: iv.report?.understandRole ?? '',
                campChoice: iv.report?.particularCamp ?? '',
                lookingForward: iv.report?.lookingForwardToInUS ?? '',
                preferences: pr,
                isDoctorLetterRequired: apl.isDoctorLetterRequired,
            },
            skills: {
                skill: sk.skills,
                skillsNote: iv.report?.applicableSkills ?? '',
            },
            experience: {
                history: occ.history ?? [],
                timeline: this.initialiseEducationWorkHistory(
                    occ.history,
                    occ.currentEducation
                ),
                references: r.references,
            },
            applicantWarning: '',
            lastPage: 0,
            reviewItems: [],
            reviewPages: [],
            assignments: assignments,
            criminalSummary: criminalSummary,
        };
        // console.log(`History: ${JSON.stringify(occ.history, null, 2)}`);
        // console.log(`read ard: ${JSON.stringify(ard, null, 2)}`);
        if (apl.fulltimeStudent) {
            const ce = occ.currentEducation;
            if (ce) {
                ard.details.collegeName = ce.collegeName
                    ? ce.collegeName
                    : ce.collegeNameOther;
                ard.details.courseName = ce.courseName;
                ard.details.dateFrom = getFormattedDate(ce.dateFrom);
                ard.details.dateTo = getFormattedDate(ce.dateTo);
            }
        } else {
            const oc = occ.occupation;
            if (oc) {
                if (!oc.occupationNameOther) {
                    ard.details.embassyPosition = oc.occupationName;
                } else {
                    ard.details.embassyPosition = oc.occupationNameOther;
                }
            }
            const wrk = occ.history?.filter(
                (h) => h.historyType === HistoryType.Work
            );
            if (wrk) {
                //if(wrk.length > 1 ) console.log(`Work: ${JSON.stringify(wrk, null, 2)}`);
                const sorted = wrk.sort((a, b) => {
                    return a.dateFrom > b.dateFrom ? -1 : 1;
                });
                if (sorted.length > 0) {
                    //console.log(`Latest work: ${JSON.stringify(sorted, null, 2)}`);
                    ard.details.establishmentName = wrk[0].name;
                    ard.details.dateFrom = getFormattedDate(
                        wrk[0].dateFrom,
                        DateFormatOptions.MonthYear
                    );
                    ard.details.dateTo = wrk[0].dateTo
                        ? getFormattedDate(wrk[0].dateTo, DateFormatOptions.MonthYear)
                        : 'present';
                }
            }
        }
        const code = app.religionCode ?? '';
        if (['CS', 'CSC', 'JN'].includes(code)) {
            ard.details.religion = app.religionName;
            ard.details.religionLevel = app.religionLevel;
        }

        if (!apl.placedAtEvent && iv.interview) {
            ard.details.interviewerId = iv.interviewer?.id ?? -1;
            ard.details.interviewerFirstName = iv.interviewer?.firstName ?? '';
            ard.details.interviewerLastName = iv.interviewer?.lastName ?? '';
            ard.details.interviewTime = iv.interview?.interviewTime;
            ard.details.interviewDate = getFormattedDate(iv.interview?.interviewDate);
            ard.details.interviewerPicture = iv.interviewer?.profileUrl;

            switch (iv.interview?.interviewType) {
                case InterviewType.Interview:
                    ard.details.interviewType = 'In-person';
                    break;
                case InterviewType.InterviewOnline:
                    ard.details.interviewType = 'Online';
                    break;
            }
            //ard.details.nonStudentSkills = iv.report?.nonStudentSkills;
            ard.details.impressions = iv.report?.firstImpression;
            ard.details.personality = iv.report?.personality;
            ard.details.whyCamp = iv.report?.whyCamp;
            ard.details.additionalDetail = iv.report?.additionalInfo;
        } else {
            ard.details.placedAtEvent = true;
        }

        ard.reviewItems = this.initialiseReviewItems(ard);
        ard.reviewPages = this.initialiseReviewPages();

        return ard;
    }

    private initialiseReviewPages(): ReviewPage[] {
        let pages = [
            new ReviewPage('profile', 'Profile'),
            new ReviewPage('background', 'Background'),
            new ReviewPage('preferences', 'Preferences'),
            new ReviewPage('skills', 'Skills'),
            new ReviewPage('experience', 'Experience'),
            new ReviewPage('final', 'Review'),
        ] as ReviewPage[];

        pages[pages.length - 1].isLast = true;
        return pages;
    }

    // MARK: Note IO

    updateReviewItems(
        applicantId: number,
        applicationId: number,
        userId: number,
        itemsOnPage: CFTextReviewItem[]
    ): Observable<boolean> {
        const s = new Subject<boolean>();

        // There might be >1 category on a given page
        const catsThisPage = [...new Set(itemsOnPage.map((iop) => iop.category))];

        const notes = itemsOnPage.map((iop) => {
            return {
                cat: iop.category,
                sub: iop.subCategory,
                itm: iop.dataItem,
                txt: iop.reviewerNote,
            };
        });

        // console.log(`Note: ${JSON.stringify(notes,null,2)}`);

        this.http
            .post<BaseResponse>(
                environment.ServiceUrl_ApplicantReview_UpdateNotes(
                    applicantId,
                    applicationId
                ),
                {
                    userId: userId,
                    cats: catsThisPage,
                    notes: notes,
                }
            )
            .subscribe({
                next: (data: BaseResponse) => {
                    s.next(data.resultCode === 1);
                },
                error: (error) => {
                    s.error(error);
                },
            });

        return s;
    }

    deleteReviewItem(
        applicantId: number,
        applicationId: number,
        userId: number,
        item: CFTextReviewItem
    ): Observable<boolean> {
        const s = new Subject<boolean>();

        this.http
            .post<BaseResponse>(
                environment.ServiceUrl_ApplicantReview_DeleteNote(
                    applicantId,
                    applicationId
                ),
                {
                    userId: userId,
                    note: {
                        cat: item.category,
                        sub: item.subCategory,
                        itm: item.dataItem,
                    },
                }
            )
            .subscribe({
                next: (data: BaseResponse) => {
                    s.next(data.resultCode === 1);
                },
                error: (error) => {
                    s.error(error);
                },
            });

        return s;
    }

    // MARK: Private

    private initialiseReviewItems(
        ard: ApplicantReviewDetails
    ): CFTextReviewItem[] {
        let items: CFTextReviewItem[] = [];
        items.push(...this.initialiseProfile(ard));
        items.push(...this.initialiseBackground(ard));
        items.push(...this.initialisePreferences(ard));
        items.push(...this.initialiseSkills(ard));
        items.push(...this.intialiseExperience(ard));

        ard.experience?.timeline.gapDisplay.forEach((gd) => {
            const status =
                gd.type === 1 ? CFTextReviewStatus.Feedback : CFTextReviewStatus.None;
            gd.reviewItem = new CFTextReviewItem(
                gd.name,
                gd.html,
                'experience',
                'history',
                CFTextReviewTarget.Applicant,
                status
            );
            items.push(gd.reviewItem);
        });

        // this.initialiseReviewNotes(ard);

        return items;
    }

    // MARK: Profile Items

    private initialiseProfile(ard: ApplicantReviewDetails): CFTextReviewItem[] {
        const pronouns = ard.details.pronouns
            ? `<span class="lp sm">${ard.details.pronouns}</span>`
            : ``;
        const previouslyKnown = ard.details.otherNameGiven
            ? `<span class="inset">Previously known as ${ard.details.otherNameGiven}`
            : ``;
        const previouslyKnowReason = ard.details.otherNameReason
            ? `</span><span class="inset">${ard.details.otherNameReason}</span>`
            : ``;
        const appCountry = this.lookup.getCountryByCode(
            ard.details.applicationCountry
        );
        let bc = this.getCountryDetails(
            ard.details.applicationCountry,
            'Born in ',
            ard.details.birthCountry
        );
        let nc = this.getCountryDetails(
            ard.details.applicationCountry,
            'Nationality ',
            ard.details.nationalityCountry
        );
        let rc = this.getCountryDetails(
            ard.details.applicationCountry,
            'Resident in ',
            ard.details.residenceCountry
        );

        const items: CFTextReviewItem[] = [
            new CFTextReviewItem(
                'Name',
                `${this.getName(
                    ard
                )}${pronouns}${previouslyKnown}${previouslyKnowReason}`,
                'profile',
                'personal',
                CFTextReviewTarget.Applicant
            ),
            new CFTextReviewItem(
                'Age',
                `${ard.details.ageOnJuneFirst
                } years old<span class="lp sm">${getFormattedDate(
                    ard.details.dateOfBirth
                )}</span>`,
                'profile',
                'personal',
                CFTextReviewTarget.Applicant
            ),
            new CFTextReviewItem(
                'Country',
                `${appCountry.name} ${appCountry.flag}${nc}${rc}${bc}`,
                'profile',
                'personal',
                CFTextReviewTarget.Applicant
            ),
        ];

        if (ard.application.isDoctorLetterRequired) {
            items.push(
                new CFTextReviewItem(
                    'Doctor letter is requested, but not uploaded',
                    'Doctor letter is required',
                    'profile',
                    'personal',
                    CFTextReviewTarget.Applicant,
                    CFTextReviewStatus.Feedback
                )
            );
        }

        const religion = ard.details.religion;
        const level = this.getReligionDescriptionFromLevel(
            ard.details.religionLevel
        );
        if (religion && level) {
            items.push(
                new CFTextReviewItem(
                    'Religion',
                    `${religion} &mdash; ${level}`,
                    'profile',
                    'personal',
                    CFTextReviewTarget.Applicant
                )
            );
        }

        if (ard.details.fulltimeStudent) {
            const edu1 = `Full-time student &mdash; <span class="sm">${ard.details.dateFrom}</span> to <span class="sm">${ard.details.dateTo}</span>`;
            const edu2 = `<span class="inset">Studying ${ard.details.courseName} at ${ard.details.collegeName}</span>`;
            items.push(
                new CFTextReviewItem(
                    'Education',
                    `${edu1}${edu2}`,
                    'profile',
                    'education',
                    CFTextReviewTarget.Applicant
                )
            );
        } else {
            const work = ard.details.establishmentName
                ? `<span class="inset">${ard.details.establishmentName} &mdash; <span class="sm">${ard.details.dateFrom}</span> to <span class="sm">${ard.details.dateTo}</span></span>`
                : 'No employment history provided';
            items.push(
                new CFTextReviewItem(
                    'Occupation',
                    `${ard.details.embassyPosition}${work}`,
                    'profile',
                    'occupation',
                    CFTextReviewTarget.Applicant
                )
            );
            // if (ard.details.nonStudentSkills) {
            //     items.push(new CFTextReviewItem('Non-student skills', ard.details.nonStudentSkills, 'profile', 'occupation', CFTextReviewTarget.Interviewer));
            // }
        }

        if (!ard.details.placedAtEvent && ard.details.whyCamp) {
            items.push(
                CFTextReviewItem.InterviewerItem(
                    undefined,
                    'Why apply',
                    ard.details.whyCamp,
                    'profile',
                    'status'
                )
            );
            //items.push(new CFTextReviewItem('Why apply', `<i class="fa-regular fa-file" style="margin-right: 1rem"></i> <b>Why apply</b> &mdash; ${ard.details.whyCamp}`, 'profile', 'interviewer', CFTextReviewTarget.Interviewer));
        }

        if (!ard.details.placedAtEvent) {
            if (ard.details.impressions) {
                items.push(
                    CFTextReviewItem.InterviewerItem(
                        'Impressions',
                        'Impressions',
                        ard.details.impressions,
                        'profile',
                        'interviewer'
                    )
                );
                //items.push(new CFTextReviewItem('Impressions', `<i class="fa-regular fa-file" style="margin-right: 1rem"></i> <b>Overall impressions</b> &mdash; ${ard.details.impressions}`, 'profile', 'interviewer', CFTextReviewTarget.Interviewer));
            }

            if (ard.details.personality) {
                items.push(
                    CFTextReviewItem.InterviewerItem(
                        'Personality',
                        'Personality',
                        ard.details.personality,
                        'profile',
                        'interviewer'
                    )
                );
                //items.push(new CFTextReviewItem('Personality', `<i class="fa-regular fa-file" style="margin-right: 1rem"></i> <b>Applicant personality</b> &mdash; ${ard.details.personality}`, 'profile', 'interviewer', CFTextReviewTarget.Interviewer));
            }

            if (ard.details.additionalDetail) {
                items.push(
                    CFTextReviewItem.InterviewerItem(
                        'Additional details',
                        'Additional details',
                        ard.details.additionalDetail,
                        'profile',
                        'interviewer'
                    )
                );
                //items.push(new CFTextReviewItem('Additional details', `<i class="fa-regular fa-file" style="margin-right: 1rem"></i> <b>Additional details</b> &mdash; ${ard.details.additionalDetail}`, 'profile', 'interviewer', CFTextReviewTarget.Interviewer));
            }
        }

        // Profile picture feedback item
        items.push(
            new CFTextReviewItem(
                'Profile picture requires attention',
                'Profile picture',
                'profile',
                'personal',
                CFTextReviewTarget.Applicant,
                CFTextReviewStatus.None
            )
        );

        return items;
    }

    private getName(ard?: ApplicantReviewDetails): string {
        if (!ard) {
            throw new Error(`No review detail specified in storage`);
        }
        const m = ard.details.middleName ? `${ard.details.middleName} ` : '';
        return `${ard.details.firstName} ${m}${ard.details.lastName}`;
    }

    private getCountryDetails(
        appCountry: string,
        title: string,
        check?: string
    ): string {
        if (!check || check === appCountry) {
            //   console.log(`Country: ${check} is undefined or matches ${appCountry}`);
            return '';
        }
        const lup = this.lookup.getCountryByCode(check);
        const flag = lup.flag !== undefined ? ` ${lup.flag}` : '';
        return `<span class="inset">${title}${lup.name}${flag}</span>`;
    }

    // MARK: Background Items

    private initialiseBackground(
        ard: ApplicantReviewDetails
    ): CFTextReviewItem[] {
        const items: CFTextReviewItem[] = [];
        if (ard.background.emergencyContactRelation) {
            items.push(
                CFTextReviewItem.ApplicantItem(
                    'Emergency contact',
                    'Emergency contact',
                    ard.background.emergencyContactRelation,
                    'background',
                    'family'
                )
            );
            // items.push(new CFTextReviewItem('Emergency contact', `<b>Emergency contact</b> &mdash; ${ard.background.emergencyContactRelation}`, 'background', 'family', CFTextReviewTarget.Applicant));
        }

        if (ard.background.interestsHobbies) {
            items.push(
                CFTextReviewItem.ApplicantItem(
                    'Family',
                    'Family',
                    ard.background.interestsHobbies,
                    'background',
                    'family'
                )
            );
            //items.push(new CFTextReviewItem('Family/interests', `<b>Family and interests</b> &mdash; ${ard.background.interestsHobbies}`, 'background', 'family', CFTextReviewTarget.Applicant));
        }

        if (!ard.details.placedAtEvent) {
            if (ard.background.family) {
                items.push(
                    CFTextReviewItem.InterviewerItem(
                        'Relationship with Family',
                        'RelationshipFamily',
                        ard.background.family,
                        'background',
                        'family'
                    )
                );
                // items.push(new CFTextReviewItem('Family', `<i class="fa-regular fa-file" style="margin-right: 1rem"></i> <b>Relationship with family</b> &mdash; ${ard.background.family}`, 'background', 'family', CFTextReviewTarget.Interviewer));
            }

            if (ard.background.friends) {
                items.push(
                    CFTextReviewItem.InterviewerItem(
                        'Relationship with Friends',
                        'RelationshipFriends',
                        ard.background.friends,
                        'background',
                        'family'
                    )
                );
                // items.push(new CFTextReviewItem('Friends', `<i class="fa-regular fa-file" style="margin-right: 1rem"></i> <b>Relationship with friends</b> &mdash; ${ard.background.friends}`, 'background', 'family', CFTextReviewTarget.Interviewer));
            }
        }

        if (ard.background.childcareExperience) {
            items.push(
                CFTextReviewItem.ApplicantItem(
                    'Childcare experience',
                    'Childcare experience',
                    ard.background.childcareExperience,
                    'background',
                    'childcare'
                )
            );
            // items.push(new CFTextReviewItem('Childcare experience', `<b>Childcare experience</b> &mdash; ${ard.background.childcareExperience}`, 'background', 'childcare', CFTextReviewTarget.Applicant));
        }

        if (!ard.details.placedAtEvent) {
            if (ard.background.childcare) {
                items.push(
                    CFTextReviewItem.InterviewerItem(
                        'Childcare experience',
                        'Childcare',
                        ard.background.childcare,
                        'background',
                        'childcare'
                    )
                );
                // items.push(new CFTextReviewItem('Childcare', `<i class="fa-regular fa-file" style="margin-right: 1rem"></i> <b>Childcare</b> &mdash; ${ard.background.childcare}`, 'background', 'childcare', CFTextReviewTarget.Interviewer));
            }
            if (ard.background.behaviour) {
                items.push(
                    CFTextReviewItem.InterviewerItem(
                        'Appropriate behaviour',
                        'Behaviour',
                        ard.background.behaviour,
                        'background',
                        'childcare'
                    )
                );
                //items.push(new CFTextReviewItem('Appropriate behaviour', `<i class="fa-regular fa-file" style="margin-right: 1rem"></i> <b>Appropriate</b> &mdash; ${ard.background.behaviour}`, 'background', 'childcare', CFTextReviewTarget.Interviewer));
            }

            if (ard.background.safeguarding) {
                items.push(
                    CFTextReviewItem.SafeGuardingItem(
                        'Safeguarding',
                        'Safeguarding',
                        ard.background.safeguarding,
                        'background',
                        'childcare'
                    )
                );
                //items.push(new CFTextReviewItem('Safeguarding', `<i class="fa-solid fa-file-shield" style="margin-right: 1rem"></i> <b>Safeguarding</b> &mdash; ${ard.background.safeguarding}`, 'background', 'childcare', CFTextReviewTarget.Safeguarding));
            }
        }
        return items;
    }

    // MARK: Preferences

    private initialisePreferences(
        ard: ApplicantReviewDetails
    ): CFTextReviewItem[] {
        const items: CFTextReviewItem[] = [];
        const programme = this.getProgramme(ard);
        const type = this.getProgrammeStatus(ard);
        const placedAtEvent = ard.details.placedAtEvent;
        items.push(
            new CFTextReviewItem(
                'Programme',
                programme,
                'preferences',
                'summary',
                CFTextReviewTarget.Interviewer
            ),
            new CFTextReviewItem(
                'Application type',
                type,
                'preferences',
                'summary',
                CFTextReviewTarget.Interviewer
            )
        );

        const availableFromDate = getDateOrDefault(
            ard.application.availableFrom,
            DateTime.now()
        );
        const june20th = DateTime.fromObject({
            year: availableFromDate.year,
            month: 6,
            day: 20,
        });

        const status =
            availableFromDate > june20th
                ? CFTextReviewStatus.Feedback
                : CFTextReviewStatus.None;

        // Create user-friendly availability text with formatted date
        const availableDateFormatted = getFormattedDate(ard.application.availableFrom);
        const data = availableFromDate > june20th
            ? `Available date is later than 20th of June (${availableDateFormatted})`
            : `Availability date requires review (${availableDateFormatted})`;
        items.push(
            new CFTextReviewItem(
                data,
                `Availability &mdash; ${getFormattedDate(
                    ard.application.availableFrom
                )}<span class="lp">Due home &mdash; ${getFormattedDate(
                    ard.application.dueHome
                )}</span>`,
                'preferences',
                'summary',
                CFTextReviewTarget.Applicant,
                status
            )
        );

        items.push(
            CFTextReviewItem.ApplicantItem(
                `${programme} role`,
                'Role',
                ard.application.role,
                'preferences',
                'interview-notes'
            )
        );
        // items.push(new CFTextReviewItem('Role', `<b>${programme} role</b> &mdash; ${ard.application.role}`, 'preferences', 'interview-notes', CFTextReviewTarget.Applicant));

        if (!placedAtEvent) {
            items.push(
                CFTextReviewItem.ApplicantItem(
                    `Looking forward to in the US`,
                    'Looking forward to',
                    ard.application.lookingForward,
                    'preferences',
                    'interview-notes'
                )
            );
            //items.push(new CFTextReviewItem('Looking forward to', `<i class="fa-regular fa-file icon-file"></i><b>Looking forward to in the US</b> &mdash; ${ard.application.lookingForward}`, 'preferences', 'interview-notes', CFTextReviewTarget.Interviewer));
        }

        if (ard.application.applicationType === ApplicationType.FirstTimer) { //returners to the same camp dont have preferences
            const pref = ard.application.preferences;
            items.push(
                new CFTextReviewItem(
                    'Underserved communities',
                    this.getCampPreference('Underserved communities', pref.underserved),
                    'preferences',
                    'camps',
                    CFTextReviewTarget.Applicant
                ),
                new CFTextReviewItem(
                    'Single-gender camps',
                    this.getCampPreference('Single gender', pref.singleGender),
                    'preferences',
                    'camps',
                    CFTextReviewTarget.Applicant
                ),
                new CFTextReviewItem(
                    'Day camps',
                    this.getCampPreference('Day camps', pref.dayCamp),
                    'preferences',
                    'camps',
                    CFTextReviewTarget.Applicant
                )
            );

            let snValues = '';
            if (pref.specialNeeds !== 'U') {
                snValues = '';
            }
            items.push(
                new CFTextReviewItem(
                    'Special-needs camp',
                    `${this.getCampPreference(
                        'Special needs',
                        pref.specialNeeds
                    )}${snValues}`,
                    'preferences',
                    'camps',
                    CFTextReviewTarget.Applicant
                )
            );

            let gsValues = '';
            if (pref.girlScout !== 'U') {
                gsValues = '';
            }
            items.push(
                new CFTextReviewItem(
                    'Girl scout camp',
                    `${this.getCampPreference('Girl scouts', pref.girlScout)}${gsValues}`,
                    'preferences',
                    'camps',
                    CFTextReviewTarget.Applicant
                )
            );

            let cValues = '';
            if (pref.christian != 'U') {
                cValues = '';
            }
            items.push(
                new CFTextReviewItem(
                    'Christian camp',
                    `${this.getCampPreference('Christian', pref.christian)}${cValues}`,
                    'preferences',
                    'camps',
                    CFTextReviewTarget.Applicant
                )
            );

            if (!placedAtEvent && ard.application.campChoice) {
                items.push(
                    CFTextReviewItem.SafeGuardingItem(
                        'Camp preferences',
                        'Camp choice',
                        ard.application.campChoice,
                        'preferences',
                        'camps'
                    )
                );
                // items.push(new CFTextReviewItem('Camp choice', `<i class="fa-solid fa-file-shield icon-file-shield"></i><b>Camp preferences</b> &mdash; ${ard.application.campChoice}`, 'preferences', 'camps', CFTextReviewTarget.Safeguarding));
            }
        }
        return items;
    }

    private getProgramme(ard?: ApplicantReviewDetails): string {
        if (!ard) {
            throw new Error(`No review detail specified in storage`);
        }
        switch (ard.application.programme) {
            case 'CC':
                return 'Counsellor';
            case 'CP':
                return 'Campower';
            case 'CC,CP':
                return 'Either';
            default:
                return ard.application.programme;
        }
        return 'Unknown';
    }

    private getProgrammeStatus(ard?: ApplicantReviewDetails): string {
        if (!ard) {
            throw new Error(`No review detail specified in storage`);
        }
        switch (ard.application.applicationType) {
            case ApplicationType.FirstTimer:
                return 'First timer';
            case ApplicationType.Repeat:
                return 'Repeat';
            case ApplicationType.Returner:
                return 'Returner';
        }
        return '';
    }

    private getReligionDescriptionFromLevel(level?: number): string {
        if (level) {
            switch (level) {
                case 1:
                    return `Does not attend services`;
                case 2:
                    return `Attends services occasionally`;
                case 3:
                    return `Willing to share faith with others`;
            }
        }
        return `Not answered`;
    }

    private getCampPreference(name: string, preference: string): string {
        let desc = 'N/A';
        switch (preference) {
            case 'W':
                desc = 'Willing';
                break;
            case 'U':
                desc = 'Unwilling';
                break;
            case 'P':
                desc = 'Preferred';
                break;
        }
        return `<b>${name}</b> &ndash; <span class="">${desc}</span>`;
    }

    private skillLevel(level: number): string {
        switch (level) {
            case 1:
                return 'General';
            case 2:
                return 'Intermediate';
            case 3:
                return 'Advanced';
        }
        return 'N/A';
    }

    private coachingLevel(level?: string): string {
        switch (level) {
            case 'I':
                return 'Informal coaching experience';
            case 'F':
                return 'Formal coaching experience';
            case 'N':
                return 'No coaching experience';
        }
        return '';
    }

    // MARK: Skills

    private initialiseSkills(ard: ApplicantReviewDetails): CFTextReviewItem[] {
        const items: CFTextReviewItem[] = [];

        const skills = ard.skills?.skill;
        if (skills) {
            skills.forEach((sk) => {
                const name = `<b>${sk.name}</b> &ndash; ${this.skillLevel(sk.rating)}`;
                const details = `<span class="inset">${sk.details}</span>`;
                let coaching = '';
                if (sk.coachingLevel === 'F' || sk.coachingLevel === 'I') {
                    coaching = `<br><span class="inset">${this.coachingLevel(
                        sk.coachingLevel
                    )} &ndash; ${sk.coachingDetails}</span>`;
                }
                items.push(
                    new CFTextReviewItem(
                        sk.name,
                        `${name}${details}${coaching}`,
                        'skills',
                        'skill-details',
                        CFTextReviewTarget.Applicant
                    )
                );
            });
        }
        if (!ard.details.placedAtEvent && ard.skills.skillsNote) {
            items.push(
                CFTextReviewItem.ApplicantItem(
                    'Skills',
                    'Skills',
                    ard.skills.skillsNote,
                    'skills',
                    'interviewer'
                )
            );
            //items.push(new CFTextReviewItem('Skills', `${ard.skills.skillsNote}`, 'skills', 'interviewer', CFTextReviewTarget.Interviewer));
        }

        return items;
    }

    // MARK: History Items

    private initialiseEducationWorkHistory(
        history?: IHistory[],
        current?: ICurrentEducation
    ): TimelineDetails {
        const entries: TimelineEntry[] = [];

        if (history) {
            // console.log(`Got history? ${JSON.stringify(history, null, 2)}`);
            if (current) {
                // console.log(`Got current? ${JSON.stringify(current, null, 2)}`);
                const ch: IHistory = {
                    historyType: 1,
                    name: current!.collegeName
                        ? current!.collegeName
                        : current!.collegeNameOther,
                    dateFrom: current!.dateFrom,
                    dateTo: current!.dateTo,
                    details: current!.courseName
                        ? `Studying ${current!.courseName}`
                        : `Current education`,
                };
                history.unshift(ch);
            }
            const sorted = history.sort((a, b) => {
                return a.dateFrom < b.dateFrom ? -1 : 1;
            });
            sorted.forEach((si) => {
                const startDateDisplay = getFormattedDate(
                    si.dateFrom,
                    DateFormatOptions.MonthYear
                );
                const endDateDisplay = si.dateTo
                    ? getFormattedDate(si.dateTo, DateFormatOptions.MonthYear)
                    : 'present';

                const startDate = getDateOrDefault(si.dateFrom, DateTime.now());
                const endDate = getDateOrDefault(si.dateTo, DateTime.now());

                entries.push(
                    new TimelineEntry(
                        si.historyType,
                        startDate,
                        startDateDisplay,
                        endDate,
                        endDateDisplay,
                        si.name,
                        si.details
                    )
                );

                // items.push(new CFTextReviewItem(si.name, `${si.name}<span class="inset sm">${getFormattedDate(si.dateFrom, DateFormatOptions.MonthYear)} &ndash; ${si.dateTo ? getFormattedDate(si.dateTo, DateFormatOptions.MonthYear) : 'present'}</span><span class="inset">${si.details}</span>`, 'experience', 'history', CFTextReviewTarget.Applicant));
            });
        }

        const tld = this.calculateTimelineRange(entries);

        return tld;
    }

    private calculateTimelineRange(timeline: TimelineEntry[]): TimelineDetails {
        let tld = new TimelineDetails();

        let start = DateTime.fromISO('2999-12-31');
        let end = DateTime.fromISO('1000-01-01');

        timeline.forEach((tl) => {
            if (tl.startDate < start) {
                start = tl.startDate;
            }
            if (tl.endDate > end) {
                end = tl.endDate;
            }
        });

        tld.from = start;
        tld.to = end;

        //const diff = end.diff(start, 'months');
        const i = Interval.fromDateTimes(start, end);
        tld.months = +i.length('months');

        for (let i = 0; i < tld.months; i++) {
            const date = start.plus({ months: i });
            const entries = timeline.filter(
                (tle) => date >= tle.startDate && date <= tle.endDate
            );
            if (entries) {
                const tl: TimelineEntry[] = entries;
                tld.entriesPerMonth.set(i, tl);
            }
        }

        this.findGaps(tld);

        return tld;
    }

    private GAP_THRESHOLD_MONTHS = 6;

    private findGaps(tld: TimelineDetails): void {
        let gapCountMonths = 0;
        let gapFound = false;
        let startGapIndex = -1;
        let endGapIndex = -1;

        const seen = new Set<number>();
        let lastItem: TimelineEntry | undefined = undefined;
        let thisItem: TimelineEntry | undefined = undefined;
        tld.entriesPerMonth.forEach((value: TimelineEntry[], key: number) => {
            // console.log(key, `${JSON.stringify(value.length)}`);
            if (value.length == 0) {
                if (!gapFound) {
                    startGapIndex = key;
                    gapFound = true;
                }
                gapCountMonths++;
                endGapIndex = key;
            } else {
                lastItem = thisItem;
                thisItem = value[0];

                if (gapFound) {
                    gapFound = false;
                    const tlg = new TimelineGap(
                        startGapIndex,
                        endGapIndex,
                        gapCountMonths
                    );
                    tld.gaps.push(tlg);
                    if (gapCountMonths >= this.GAP_THRESHOLD_MONTHS) {
                        const g = `Gap between: ${lastItem?.endDateDisplay} and ${thisItem?.startDateDisplay}`;
                        tld.gapDisplay.push(
                            new GapEntry(
                                g,
                                1,
                                `<span class="gap">${gapCountMonths} month gap between entries</span>`
                            )
                        );
                    }
                    gapCountMonths = 0;
                }
                value.forEach((e) => {
                    // Is it the first occurence?
                    if (!seen.has(e.id)) {
                        seen.add(e.id);
                        tld.gapDisplay.push(
                            new GapEntry(
                                e.title,
                                0,
                                this.getTimelineEntryHtml(e),
                                e.description
                            )
                        );
                    }
                });
            }
        });

        // tld.gaps.forEach(g => {
        //     console.warn(`We found a gap of ${g.gapLength} between indices ${g.startIndex} and ${g.endIndex}!`);
        // });
    }

    private getTimelineEntryHtml(e: TimelineEntry): string {
        const start = e.startDate.toFormat('LLLL yyyy');
        const end = e.endDate ? e.endDate.toFormat('LLLL yyyy') : 'Present';
        const time = `${start} - ${end}`;

        const type =
            e.type == 1
                ? `<i class="fa-solid fa-graduation-cap"></i>`
                : `<i class="fa-regular fa-building"></i>`;

        const note = e.note ? `<span class="note">(${e.note})</span>` : '';

        return `<div class="tle"><p class="est">${e.title}${note}</p><p class="type">${type}</p></div><p class="dt">${time}</p>`;

        //return `<p class='title'>${type}${e.title}</p><p class='date'>${time}</p><p class='description'>${e.description}</p>`;
    }

    // MARK: Experience

    private intialiseExperience(ard: ApplicantReviewDetails): CFTextReviewItem[] {
        const items: CFTextReviewItem[] = [];

        const references = ard.experience?.references;
        if (references) {
            references.forEach((r, index) => {
                const name = `${r.firstName} ${r.lastName}`;
                const pos = r.position;
                const dateFrom = getFormattedDate(
                    r.dateFrom,
                    DateFormatOptions.MonthYear
                );
                const dateTo = getFormattedDate(r.dateTo, DateFormatOptions.MonthYear);
                const dates =
                    `${dateFrom}` +
                    (r.dateTo ? ` &ndash; ${dateTo}` : ` &ndash; present`);
                // TODO: Source?
                let source = '';
                if (r.sourceType) {
                    source = this.references.getReferenceSource(r.sourceType);
                }
                let display = `${name}<br>${pos}<br>${dates}${source !== 'Other' ? `<br>${source}` : ''
                    }`;

                // Create a unique dataItem for each reference using name, dates, and position info (stable across reloads)
                const uniqueRefId = `${name}_${r.dateFrom}_${r.dateTo || 'present'}_${r.position}_${r.sourceType || 'unknown'}`;

                // ABE-139: don't add twice if status is 1...
                if (r.status !== 1) {
                    items.push(
                        new CFTextReviewItem(
                            `Referee ${uniqueRefId}`,
                            display,
                            'experience',
                            'referee',
                            CFTextReviewTarget.Applicant
                        )
                    );
                } else if (r.status === 1) {
                    //status requested
                    // Keep the unique ID for internal tracking, but use clean display name
                    items.push(
                        new CFTextReviewItem(
                            `Reference is in status Requested_${uniqueRefId}`,
                            display,
                            'experience',
                            'referee',
                            CFTextReviewTarget.Applicant,
                            CFTextReviewStatus.Feedback
                        )
                    );
                }
            });
        }

        if (
            (!references || references.length == 0) &&
            ReviewService.MinimumReferences > 0
        ) {
            items.push(
                new CFTextReviewItem(
                    'No references added',
                    'No references added',
                    'experience',
                    'referee',
                    CFTextReviewTarget.Applicant,
                    CFTextReviewStatus.Feedback
                )
            );
        }

        if (
            references &&
            references?.length > 0 &&
            references?.length < ReviewService.MinimumReferences
        ) {
            items.push(
                new CFTextReviewItem(
                    `Fewer than ${ReviewService.MinimumReferences} references added`,
                    `Fewer than ${ReviewService.MinimumReferences} references added`,
                    'experience',
                    'referee',
                    CFTextReviewTarget.Applicant,
                    CFTextReviewStatus.Feedback
                )
            );
        }

        return items;
    }

    // MARK: Review Pages

    getReviewerNotes(
        applicantId: number,
        applicationId: number
    ): Observable<ReviewerNote[]> {
        const s = new Subject<ReviewerNote[]>();
        this.http
            .get<BaseResponse>(
                environment.ServiceUrl_ApplicantReview_ReadNotes(
                    applicantId,
                    applicationId
                )
            )
            .subscribe({
                next: (r: BaseResponse) => {
                    const d = this.getResultData<ReviewerNoteResponse>(r);
                    s.next(d.notes);
                    // ForkJoin requires the observable to Complete
                    s.complete();
                },
                error: (error: any) => s.error(error),
            });

        return s;
    }

    static SessionApplicantToReview = 's:jtr:review';
    static SessionReviewNotes = 's:rvn';

    static MinimumReferences = 2;
}

// MARK: Interfaces

export interface ApplicantReviewDetails {
    details: IApplicationReviewProfile;
    background: IApplicationReviewBackground;
    application: IApplicationReviewApplication;
    skills: IApplicationReviewSkills;
    experience?: IApplicationReviewExperience;

    // Any warnings
    applicantWarning?: string;

    // Probably need to break out other details into separate classes/internal structures

    // Used by the review process internally
    reviewPages: ReviewPage[];
    reviewItems: CFTextReviewItem[];
    lastPage: number;

    reviewerNotes: ReviewerNote[];

    assignments: WorkAreaApplicationAssignment[];
    criminalSummary: IApplicationCriminalSummary;
}

export interface ReviewerNoteResponse {
    notes: ReviewerNote[];
}

export interface ReviewerNote {
    cat: string;
    sub: string;
    itm: string;
    txt: string;
    userId: number;
    fullName: string;
}

export enum ReviewPageStatus {
    Unreviewed = 'unreviewed-fg',
    Good = 'done-fg',
    Issue = 'warn-fg',
    Note = 'todo-fg',
}

export class ReviewPage {
    route: string;
    alt: string;
    status: ReviewPageStatus;
    viewed: boolean = false;
    isFirst = false;
    isLast = false;
    constructor(route: string, name: string, status?: ReviewPageStatus) {
        this.route = route;
        this.alt = name;
        this.status = status ?? ReviewPageStatus.Unreviewed;
    }
}

export class ReviewNotes {
    name: string;
    category: string;
    subCategory: string;
    target: CFTextReviewTarget;
    reason: string;
    reasonId: number = 0;

    constructor(
        name: string,
        target: CFTextReviewTarget,
        reason?: string,
        reasonId?: number
    ) {
        this.name = name;
        this.category = '';
        this.subCategory = '';
        this.target = target;
        this.reason = reason ?? '';
        this.reasonId = reasonId ?? 0;
    }
}

export class ReviewInterviewDetails {
    interviewerId?: number;
    interviewerProfile?: string;
    interviewerName?: string;
    interviewDate?: string;
    interviewTime?: string;
    interviewType?: string;
    interviewCopied?: boolean;
}
